// Thin client for our server (same origin). Session = HttpOnly cookie; errors come back as { error, message, details }.
import { visitId } from './analytics';

export class ApiError extends Error {
  constructor(public status: number, public code: string, public details?: unknown) {
    super(code);
  }
}

type Opts = { method?: string; body?: unknown; signal?: AbortSignal };

export async function api<T = unknown>(path: string, opts: Opts = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const v = visitId();
  if (v) headers['X-Visit-Id'] = v;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'offline');
  }
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* not json */ }
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new CustomEvent('bp:unauthorized'));
    throw new ApiError(res.status, data?.error ?? `http_${res.status}`, data?.details);
  }
  return data as T;
}

// ---------- shared types (server contract, docs/api-families-tracker.md) ----------
export type Features = { baby_access_app: boolean; baby_access_coach: boolean; baby_access_book: boolean };
export type Me = {
  id: string;
  email: string;
  name: string | null;
  locale: string;
  units: 'imperial' | 'metric';
  features: Features;
  /** AI chat messages left from packs: the chat opens with them even without the add-on. */
  coachCredits: number;
  /** PDF volumes whose file is ready: download at /api/books/<id>/download (book purchase only, kept forever). */
  booksReady?: string[];
  /** PDF volumes this person bought (each has its own access; a bundle opens all). */
  booksOwned?: string[];
  healthConsent: { given: boolean; version: string | null; current: string; needsUpdate: boolean };
  notifyPrefs: { napReminder: boolean; bedtimeReminder: boolean; leadMinutes: number; quietStart?: string | null; quietEnd?: string | null };
  onboarding: { completedAt?: string; babyId?: string; plan?: unknown } & Record<string, unknown>;
};
export type Age = {
  days: number; weeks: number; months: number; wholeMonths: number;
  useCorrected: boolean; correctedWeeks: number; correctedMonths: number; ageForSleepDays: number;
};
export type Baby = {
  id: string; familyId: string; name: string; birthDate: string; dueDate: string | null; sex: string | null;
  planSettings: { targetWakeTime?: string; targetBedtime?: string };
  age: Age;
};
export type Member = { userId: string; name: string | null; email?: string; role: 'owner' | 'partner' | 'caregiver' };
export type Family = { id: string; timezone: string; ownerId: string; role: Member['role']; members: Member[]; babies: Baby[] };

export const getMe = () => api<{ user: Me }>('/api/me').then((r) => r.user);
export const getFamilies = () => api<{ families: Family[] }>('/api/families').then((r) => r.families);
